import React from "react";
import ReactDOM from "react-dom";
import { useCallback, useContext, useRef, useState } from "react";
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faPaperclip,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import CloseIcon from "assets/icons/close.svg?react";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import PDFViewer from "components/PDFViewer";
import ModalsManagerContext, { ContractRegistryModalOptions } from "contexts/ModalsManager";
import contractsService, { ICreateContractPayload } from "api/contractsService";
import budgetService, { DEFAULT_BUDGET_YEAR } from "api/budgetService";
import {
  ContractNoticePeriod,
  IContractParty,
} from "dto/Contracts/IContract";
import { IBudgetExpense } from "dto/Budget/IBudgetExpense";
import { IBudgetEngagement } from "dto/Budget/IBudgetEngagement";
import { ENotificationType, notification } from "utils/notification";
import usePDF from "hooks/usePDF";
import styles from "./styles.module.scss";
import moment from "moment";
import CollapsibleSection from "./CollapsibleSection";
import AmountField from "./AmountField";
import parseAmount from "./parseAmount";
import ConsortiumPartyCard from "./ConsortiumPartyCard";
import PartiesCard from "./PartiesCard";

const NOTICE_PERIOD_OPTIONS: { value: ContractNoticePeriod; label: string }[] =
  [
    { value: "Week", label: "Tydzień" },
    { value: "TwoWeeks", label: "Dwa tygodnie" },
    { value: "Month", label: "Miesiąc" },
    { value: "ThreeMonths", label: "3 miesiące" },
    { value: "Other", label: "Inne" },
  ];

interface ConsortiumEntry { id: string; name: string; members: IContractParty[] }

const CHANGE_TYPE_OPTIONS = [
  "Korekta danych",
  "Aneks do umowy",
  "Zmiana w zakresie",
  "Wyłączenia jawności",
  "Zmiana danych strony umowy",
  "Rozwiązanie umowy",
  "Wypowiedzenie umowy",
  "Odstąpienie od umowy",
  "Wygaśnięcie umowy",
  "Cesja umowy",
  "Inne",
];

const CONFIDENTIALITY_BASES = [
  { value: "DIP_art5_ust1",  label: "Art. 5 ust. 1"  },
  { value: "DIP_art5_ust2",  label: "Art. 5 ust. 2"  },
  { value: "DIP_art5_ust2a", label: "Art. 5 ust. 2a" },
  { value: "DIP_art5_ust2b", label: "Art. 5 ust. 2b" },
  { value: "Other",          label: "Inna podstawa wyłączenia jawności" },
];


const CreateRegistryEntryModal = () => {
  const { setModals, modals, options } = useContext(ModalsManagerContext);
  const refWrapper = useRef(null);

  // Contract fields
  const [contractNumber, setContractNumber] = useState("");
  const [signedDate, setSignedDate] = useState<moment.Moment | null>(moment());
  const [subject, setSubject] = useState("");
  const [subjectHidden, setSubjectHidden] = useState(false);
  const [subjectBasis, setSubjectBasis] = useState("");
  const [amountHidden, setAmountHidden] = useState(false);
  const [amountBasis, setAmountBasis] = useState("");
  const [netAmount, setNetAmount] = useState("");
  const [grossAmount, setGrossAmount] = useState("");
  const [durationType, setDurationType] = useState<"FixedTerm" | "Indefinite">(
    "Indefinite"
  );
  const [durationUntil, setDurationUntil] = useState<moment.Moment | null>(
    null
  );
  const [noticePeriod, setNoticePeriod] =
    useState<ContractNoticePeriod>("Month");
  const [noticePeriodDescription, setNoticePeriodDescription] = useState("");
  const [parties, setParties] = useState<IContractParty[]>([]);
  const [consortiumItems, setConsortiumItems] = useState<ConsortiumEntry[]>([]);

  const [contractFile, setContractFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<"new" | "change">("new");
  const [changeType, setChangeType] = useState("");

  // Powiąż z budżetem
  const [budgetExpense, setBudgetExpense] = useState<IBudgetExpense | null>(
    null
  );
  const [budgetEngagement, setBudgetEngagement] =
    useState<IBudgetEngagement | null>(null);

  const preloadedFileOpts =
    options instanceof ContractRegistryModalOptions && options.file
      ? options
      : null;

  const [openSection, setOpenSection] = useState<string | null>(
    preloadedFileOpts ? "basic" : "file"
  );
  const toggleSection = (key: string) =>
    setOpenSection((prev) => (prev === key ? null : key));

  const { blob, addBlob, removeBlob } = usePDF();

  // Pre-populate file when opened from PDF preview
  React.useEffect(() => {
    if (!preloadedFileOpts) return;
    const blob = preloadedFileOpts.file!;
    const name = preloadedFileOpts.fileName ?? "dokument.pdf";
    const file = new File([blob], name, { type: blob.type || "application/pdf" });
    setContractFile(file);
    addBlob(blob, name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutate: createContract, isLoading } =
    contractsService.useCreateContract();

  const { data: budgetExpenses = [] } =
    budgetService.useGetExpenses(DEFAULT_BUDGET_YEAR);
  const { data: budgetEngagements = [] } = budgetService.useGetEngagements(
    budgetExpense?.Id ?? "",
    !!budgetExpense
  );

  const handleClose = () => {
    setModals({ ...modals, hide: true });
  };

  // ---- Consortium helpers ----
  const addConsortium = () =>
    setConsortiumItems((prev) => [...prev, { id: `c-${Date.now()}`, name: "", members: [] }]);

  const removeConsortium = (idx: number) =>
    setConsortiumItems((prev) => prev.filter((_, i) => i !== idx));

  const updateConsortiumData = (idx: number, name: string, members: IContractParty[]) =>
    setConsortiumItems((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, name, members } : item))
    );

  // ---- File upload ----
  const onDrop = useCallback(
    (accepted: File[]) => {
      if (!accepted.length) return;
      const file = accepted[0];
      setContractFile(file);
      addBlob(new Blob([file], { type: file.type }), file.name);
    },
    [addBlob],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
  });

  // ---- Submit ----
  const canSubmit =
    !!contractNumber.trim() &&
    (durationType === "Indefinite" || !!durationUntil?.isValid());

  const handleSubmit = () => {
    if (!canSubmit) return;

    const net = parseAmount(netAmount);
    const gross = parseAmount(grossAmount);

    const payload: ICreateContractPayload = {
      ContractNumber: contractNumber.trim(),
      SignedDate: signedDate?.isValid() ? signedDate.toISOString() : new Date().toISOString(),
      Parties: [
        ...parties
          .filter((p) => p.Name.trim() || p.Role.trim())
          .map((p) => ({ ...p, IdentifierValue: p.IdentifierValue.replace(/\D/g, "") })),
        ...consortiumItems.flatMap((item) =>
          item.members
            .filter((m) => m.Name.trim() || m.Role.trim())
            .map((m) => ({
              ...m,
              IdentifierValue: m.IdentifierValue.replace(/\D/g, ""),
              ConsortiumName: item.name || undefined,
            }))
        ),
      ],
      Subject: subject.trim(),
      ...(subjectHidden && subjectBasis && { SubjectConfidentialityBasis: subjectBasis }),
      NetAmount: net,
      VatAmount: Math.round((gross - net) * 100) / 100,
      GrossAmount: gross,
      ...(amountHidden && amountBasis && { AmountConfidentialityBasis: amountBasis }),
      Duration:
        durationType === "Indefinite"
          ? { Type: "Indefinite" as const }
          : { Type: "FixedTerm" as const, Until: durationUntil!.toISOString() },
      ...(durationType === "Indefinite" && {
        NoticePeriod: noticePeriod,
        ...(noticePeriod === "Other" && {
          NoticePeriodDescription: noticePeriodDescription.trim(),
        }),
      }),
      ...(budgetExpense && { BudgetExpenseId: budgetExpense.Id }),
      ...(budgetEngagement && { BudgetEngagementId: budgetEngagement.Id }),
      file: contractFile,
    };

    createContract(payload, {
      onSuccess: () => {
        notification({
          title: "Sukces",
          text: "Umowa została dodana do rejestru.",
          type: ENotificationType.INFO,
        });
        handleClose();
      },
      onError: (err: unknown) => {
        type ApiErr = { response?: { data?: { message?: string; title?: string } }; message?: string };
        const e = err as ApiErr;
        notification({
          title: "Błąd",
          text:
            e?.response?.data?.message ??
            e?.response?.data?.title ??
            e?.message ??
            "Nie udało się dodać umowy do rejestru.",
          type: ENotificationType.ERROR,
        });
      },
    });
  };

  return (
    <>
    <ModalLayout
      name="createContractEntry"
      dock={DockPosition.DISABLED}
      off={true}
      customX={8}
      customY={0}
      ignore
      refWrapper={refWrapper}
      isAnimation
      fullHeight={true}
    >
      <>
        <div className={styles.wrapper}>
          <div className={styles.title}>
            Umowa
            <CloseIcon className={styles.closeIcon} onClick={handleClose} />
          </div>

          <div className={styles.content}>
            <>
              {/* Plik umowy — drag & drop; ukryte gdy plik pochodzi z podglądu przesyłki */}
              {!preloadedFileOpts && <CollapsibleSection title="Dodaj plik" isOpen={openSection === "file"} onToggle={() => toggleSection("file")} bodyClassName={styles.sectionBodyDense}>
                  {/* Typ pliku */}
                  <div className={styles.fileTypeRow}>
                    <RadioGroup
                      row
                      value={fileType}
                      onChange={(e) => {
                        setFileType(e.target.value as "new" | "change");
                        setChangeType("");
                      }}
                    >
                      <FormControlLabel
                        value="new"
                        control={<Radio size="small" />}
                        label="Nowa umowa"
                      />
                      <FormControlLabel
                        value="change"
                        control={<Radio size="small" />}
                        label="Zmiana warunków umowy"
                      />
                    </RadioGroup>
                    {fileType === "change" && (
                      <FormControl size="small" className={styles.changeTypeSelect}>
                        <InputLabel>Rodzaj zmiany</InputLabel>
                        <Select
                          value={changeType}
                          label="Rodzaj zmiany"
                          onChange={(e) => setChangeType(e.target.value)}
                        >
                          {CHANGE_TYPE_OPTIONS.map((o) => (
                            <MenuItem key={o} value={o}>{o}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </div>

                  {!contractFile ? (
                    <div
                      {...getRootProps()}
                      className={`${styles.dropzone} ${
                        isDragActive ? styles.dropzoneActive : ""
                      }`}
                    >
                      <input {...getInputProps()} />
                      <FontAwesomeIcon
                        icon={faPaperclip}
                        className={styles.dropzoneIcon}
                      />
                      <span className={styles.dropzoneText}>
                        Przeciągnij plik lub{" "}
                        <span className={styles.dropzoneLink}>pobierz z dysku</span>
                      </span>
                      <span className={styles.dropzoneHint}>PDF, DOC, DOCX</span>
                    </div>
                  ) : (
                    <div className={styles.filePreview}>
                      <FontAwesomeIcon
                        icon={faPaperclip}
                        className={styles.fileIcon}
                      />
                      <span className={styles.fileName}>{contractFile.name}</span>
                      <button
                        className={styles.filePreviewBtn}
                        onClick={() =>
                          addBlob(
                            new Blob([contractFile], { type: contractFile.type }),
                            contractFile.name,
                          )
                        }
                        title="Podgląd"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        className={styles.fileRemove}
                        onClick={() => {
                          setContractFile(null);
                          removeBlob();
                        }}
                        title="Usuń plik"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </CollapsibleSection>}

                {/* Dane podstawowe — zwijana */}
                <CollapsibleSection title="Dane podstawowe" isOpen={openSection === "basic"} onToggle={() => toggleSection("basic")} bodyClassName={styles.sectionBodyDense}>
                  {/* Numer umowy + Data zawarcia w jednym wierszu */}
                  <div className={styles.row2}>
                    <TextField
                      label="Numer umowy"
                      size="small"
                      fullWidth
                      value={contractNumber}
                      onChange={(e) => setContractNumber(e.target.value)}
                    />
                    <DatePicker
                      label="Data zawarcia"
                      value={signedDate}
                      onChange={(v) => setSignedDate(v)}
                      slotProps={{ textField: { size: "small", fullWidth: true } }}
                    />
                  </div>

                  <div className={styles.subjectLabelRow}>
                    <span className={styles.sectionLabel}>Przedmiot umowy</span>
                    <button
                      type="button"
                      className={`${styles.hideToggleBtn} ${subjectHidden ? styles.hideToggleBtnActive : ""}`}
                      onClick={() => {
                        setSubjectHidden((h) => !h);
                        if (subjectHidden) setSubjectBasis("");
                      }}
                    >
                      {subjectHidden ? "Cofnij" : "Ukryj"}
                    </button>
                    {subjectHidden && (
                      <FormControl size="small" className={styles.subjectBasisSelect}>
                        <InputLabel>Podstawa wyłączenia jawności</InputLabel>
                        <Select
                          value={subjectBasis}
                          label="Podstawa wyłączenia jawności"
                          onChange={(e) => setSubjectBasis(e.target.value)}
                        >
                          {CONFIDENTIALITY_BASES.map((b) => (
                            <MenuItem key={b.value} value={b.value}>
                              {b.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </div>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />

                  <div className={styles.subjectLabelRow}>
                    <span className={styles.sectionLabel}>Wartość umowy</span>
                    <button
                      type="button"
                      className={`${styles.hideToggleBtn} ${amountHidden ? styles.hideToggleBtnActive : ""}`}
                      onClick={() => {
                        setAmountHidden((h) => !h);
                        if (amountHidden) setAmountBasis("");
                      }}
                    >
                      {amountHidden ? "Cofnij" : "Ukryj"}
                    </button>
                    {amountHidden && (
                      <FormControl size="small" className={styles.subjectBasisSelect}>
                        <InputLabel>Podstawa wyłączenia jawności</InputLabel>
                        <Select
                          value={amountBasis}
                          label="Podstawa wyłączenia jawności"
                          onChange={(e) => setAmountBasis(e.target.value)}
                        >
                          {CONFIDENTIALITY_BASES.map((b) => (
                            <MenuItem key={b.value} value={b.value}>
                              {b.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </div>
                  <div className={styles.row2}>
                    <AmountField
                      label="Netto"
                      liveFormat
                      value={netAmount}
                      onChange={setNetAmount}
                    />
                    <AmountField
                      label="Brutto"
                      liveFormat
                      value={grossAmount}
                      onChange={setGrossAmount}
                    />
                  </div>

                  <div className={styles.sectionLabel}>Czas obowiązywania umowy</div>
                  <RadioGroup
                    row
                    value={durationType}
                    onChange={(e) =>
                      setDurationType(
                        e.target.value as "FixedTerm" | "Indefinite"
                      )
                    }
                  >
                    <FormControlLabel
                      value="Indefinite"
                      control={<Radio size="small" />}
                      label="Bezterminowa"
                    />
                    <FormControlLabel
                      value="FixedTerm"
                      control={<Radio size="small" />}
                      label="Na czas określony do"
                    />
                  </RadioGroup>
                  {durationType === "Indefinite" && (
                    <div className={styles.durationExtra}>
                      <FormControl size="small" sx={{ width: "calc(50% - 5px)" }}>
                        <InputLabel>Okres wypowiedzenia</InputLabel>
                        <Select
                          value={noticePeriod}
                          label="Okres wypowiedzenia"
                          onChange={(e) =>
                            setNoticePeriod(
                              e.target.value as ContractNoticePeriod
                            )
                          }
                        >
                          {NOTICE_PERIOD_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {noticePeriod === "Other" && (
                        <TextField
                          label="Opis okresu wypowiedzenia"
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          value={noticePeriodDescription}
                          onChange={(e) =>
                            setNoticePeriodDescription(e.target.value)
                          }
                        />
                      )}
                    </div>
                  )}
                  {durationType === "FixedTerm" && (
                    <div className={styles.durationExtra}>
                      <DatePicker
                        label="Data wygaśnięcia"
                        value={durationUntil}
                        onChange={(v) => setDurationUntil(v)}
                        slotProps={{
                          textField: { size: "small", sx: { width: "calc(50% - 5px)" } },
                        }}
                      />
                    </div>
                  )}
                </CollapsibleSection>

                {/* Strony umowy */}
                <CollapsibleSection title="Strony umowy" isOpen={openSection === "parties"} onToggle={() => toggleSection("parties")} bodyClassName={styles.sectionBodyDense}>
                  <PartiesCard
                    onDataChange={(p) => setParties(p)}
                  />

                  {/* Konsorcja */}
                  {consortiumItems.map((item, idx) => (
                    <ConsortiumPartyCard
                      key={item.id}
                      onDataChange={(name, members) => updateConsortiumData(idx, name, members)}
                      onRemove={() => removeConsortium(idx)}
                    />
                  ))}

                  <button className={styles.addPartyBtn} onClick={addConsortium}>
                    + Dodaj konsorcjum
                  </button>
                </CollapsibleSection>

                {/* Powiąż z budżetem — zwijana */}
                <CollapsibleSection title="Powiąż z budżetem" isOpen={openSection === "budget"} onToggle={() => toggleSection("budget")} bodyClassName={styles.sectionBodyDense}>
                  <Autocomplete
                    options={budgetExpenses}
                    value={budgetExpense}
                    getOptionLabel={(o) => o.Title}
                    isOptionEqualToValue={(a, b) => a.Id === b.Id}
                    onChange={(_, next) => {
                      setBudgetExpense(next);
                      setBudgetEngagement(null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Powiąż z zadaniem"
                        placeholder="Wybierz zadanie"
                      />
                    )}
                  />
                  <Autocomplete
                    options={budgetEngagements}
                    value={budgetEngagement}
                    disabled={!budgetExpense}
                    getOptionLabel={(o) => o.Title}
                    isOptionEqualToValue={(a, b) => a.Id === b.Id}
                    onChange={(_, next) => setBudgetEngagement(next)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Powiąż z zaangażowaniem"
                        placeholder={
                          budgetExpense
                            ? "Wybierz zaangażowanie"
                            : "Najpierw wybierz zadanie"
                        }
                      />
                    )}
                  />
                </CollapsibleSection>
            </>
          </div>

          <div className={styles.footer}>
            <button
              className={styles.submit}
              disabled={!canSubmit || isLoading}
              onClick={handleSubmit}
            >
              Zapisz w rejestrze
            </button>
          </div>
        </div>
      </>
    </ModalLayout>
    {blob &&
      ReactDOM.createPortal(
        <div className={styles.pdfPreviewPanel}>
          <PDFViewer
            file={blob}
            title="Podgląd umowy"
            showCloseButton={true}
            onCloseButton={removeBlob}
          />
        </div>,
        document.body,
      )}
    </>
  );
};

export default CreateRegistryEntryModal;
